<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mb-8">
      <div class="bg-blue-50 p-6 rounded-t-lg">
        <div class="flex items-center mb-1">
          <div class="w-8 h-8 rounded-lg flex items-center justify-start">
            <BookOpenIcon class="w-6 h-6 text-blue-800" />
          </div>
          <h2 class="text-2xl font-semibold text-blue-900">Session History</h2>
        </div>
        <p class="text-blue-800 font-semibold">Manage your tutoring sessions.</p>
      </div>

      <div class="p-6">
        <!-- Search and Filter Section - Always visible -->
        <SessionSearchAndFilter
          v-model:searchQuery="searchQuery"
          v-model:statusFilter="statusFilter"
          v-model:subjectFilter="subjectFilter"
          filter-context="history"
        />

        <!-- No Sessions State - Only show when there are no sessions at all -->
        <div v-if="props.completedSessions.length === 0 && props.cancelledSessions.length === 0" class="bg-white rounded-lg pb-5 px-6 mt-6">
          <div class="text-center py-12">
            <CalendarIcon class="mx-auto h-12 w-12 text-gray-400" />
            <h3 class="mt-2 text-sm font-medium text-gray-900">No session history yet</h3>
            <p class="mt-1 text-sm text-gray-500">When you complete or cancel sessions, they'll appear here.</p>
          </div>
        </div>

        <!-- Completed Sessions Section -->
        <div v-if="props.completedSessions.length > 0" class="bg-white rounded-lg pb-5 px-6 mb-6 mt-6">
          <!-- Header with count -->
          <div class="flex items-center gap-3 mb-6">
            <div class="w-3 h-3 bg-green-500 rounded-full"></div>
            <h3 class="text-lg font-semibold text-gray-900">
              Completed Sessions ({{ filteredCompletedSessions.length }})
            </h3>
          </div>

          <!-- No filtered results for completed sessions -->
          <div v-if="filteredCompletedSessions.length === 0" class="text-center py-8">
            <CalendarIcon class="mx-auto h-12 w-12 text-gray-400" />
            <h3 class="mt-2 text-sm font-medium text-gray-900">No completed sessions match your search</h3>
            <p class="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
          </div>

          <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div
              v-for="session in paginatedCompletedSessions"
              :key="session.id"
              @click="openSessionDetails(session)"
              class="bg-white border-2 border-black rounded-lg p-6 shadow-[4px_4px_0_0_rgba(0,0,0,1)] transition-all duration-300 hover:scale-102 cursor-pointer"
            >
              <!-- Session Header -->
              <div class="mb-4">
                <!-- Desktop: Student Info + Status Tags in same row -->
                <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                  <!-- Student Info -->
                  <div class="flex items-center gap-3">
                    <UserAvatar
                      :src="session.studentProfileImage"
                      :name="session.studentName"
                      size="md"
                    />
                    <div class="flex-1">
                      <h4 class="font-medium text-gray-900">{{ session.studentName }}</h4>
                    </div>
                  </div>
                  
                  <!-- Status Tags - Desktop: upper right, Mobile: below -->
                  <div class="flex items-center gap-2 flex-shrink-0">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 border-2 border-blue-800 text-blue-800 whitespace-nowrap"
                    >
                      Completed
                    </span>
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                      :class="getTypeClasses(session.type)"
                    >
                      {{ session.type === "face-to-face" ? "Face-to-Face" : "Online" }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Session Details -->
              <div class="space-y-3 mb-4">
                <div class="flex items-center gap-2 text-md text-gray-600">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  <span>{{ session.subject }}</span>
                </div>

                <div
                  class="flex justify-between border-2 border-blue-800 bg-blue-50/50 rounded-lg py-2 px-4"
                >
                  <div class="flex items-center gap-2 text-sm text-blue-800">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span class="font-semibold">{{ session.date }}</span>
                  </div>

                  <div class="flex items-center gap-2 text-sm text-blue-800">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span class="font-semibold">{{ session.time }}</span>
                  </div>
                </div>

                <div
                  v-if="session.location"
                  class="flex items-center gap-2 border border-gray-200 bg-gray-50/50 rounded-sm py-2 px-4 text-sm text-gray-600 min-w-0"
                >
                  <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span class="truncate" :title="session.location">{{ session.location }}</span>
                </div>
              </div>

              <!-- Ratings -->
              <div class="mb-4">
                <div class="text-sm mb-2">
                  <span class="text-gray-600"
                    >Your Rating:
                    <span class="font-semibold text-green-700">
                      {{
                        session.yourRating && session.yourRating !== "0"
                          ? session.yourRating + "/5.0"
                          : "No rate given yet"
                      }}
                    </span>
                  </span>
                </div>
                <div class="text-sm">
                  <span class="text-gray-600"
                    >Student's Rating:
                    <span class="font-semibold text-blue-700">
                      {{
                        session.studentRating && session.studentRating !== "0"
                          ? session.studentRating + "/5.0"
                          : "No rate given yet"
                      }}
                    </span>
                  </span>
                </div>
              </div>

              <!-- Feedback -->
              <div
                v-if="session.feedbackStatus === 'pending'"
                class="mb-4 p-3 rounded-md bg-yellow-50 border border-yellow-200"
              >
                <div class="text-sm">
                  <span class="font-medium text-gray-600">Pending Feedback</span>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="space-y-3" @click.stop>
                <!-- Row: Give Feedback and Message (side by side) -->
                <div class="flex gap-2">
                  <button
                    v-if="
                      session.feedbackStatus !== 'given' ||
                      !session.yourRating ||
                      session.yourRating === '0'
                    "
                    @click="navigateToFeedback(session.id)"
                    class="flex-1 px-3 py-2 bg-white border-2 border-orange-700 text-orange-800 rounded-md shadow-[4px_4px_0_0_rgba(194,65,12,1)] hover:bg-orange-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <StarIcon class="w-4 h-4" />
                    Give Feedback
                  </button>
                  <span
                    v-else
                    class="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-400 border-2 border-gray-300 cursor-not-allowed"
                  >
                    <StarIcon class="w-4 h-4 mr-2" />
                    Feedback Given
                  </span>
                  <button
                    class="flex-1 px-3 py-2 bg-white border-2 border-blue-800 text-blue-800 rounded-md shadow-[4px_4px_0_0_rgba(30,64,175,1)] hover:bg-blue-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    Message
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="props.completedSessions.length > 0 && filteredCompletedSessions.length > 0 && totalPages > 1" class="bg-white rounded-lg mt-4 pb-3 sm:pb-5 px-3 sm:px-6">
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
            <div class="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
              Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ filteredCompletedSessions.length }} completed sessions
            </div>
            <div class="flex items-center space-x-1 overflow-x-auto w-full sm:w-auto order-1 sm:order-2">
              <button
                @click="previousPage"
                :disabled="currentPage === 1"
                class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
                title="Previous page"
              >
                <ChevronLeftIcon class="w-3 h-3 sm:w-4 sm:h-4" />
                <span class="hidden sm:inline">Previous</span>
                <span class="sm:hidden">Prev</span>
              </button>
              <button
                v-for="page in totalPages"
                :key="page"
                @click="goToPage(page)"
                :class="[
                  'px-2 sm:px-3 py-1 rounded-md border-2 border-blue-800 transition-colors min-w-[32px] sm:min-w-[40px] text-xs sm:text-sm flex-shrink-0',
                  page === currentPage
                    ? 'bg-blue-600 text-white font-semibold'
                    : 'text-blue-700 hover:bg-blue-50',
                ]"
              >
                {{ page }}
              </button>
              <button
                @click="nextPage"
                :disabled="currentPage === totalPages"
                class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
                title="Next page"
              >
                <span class="hidden sm:inline">Next</span>
                <span class="sm:hidden">Next</span>
                <ChevronRightIcon class="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- Cancelled Sessions Section -->
        <CancelledSessionsHistory
          :sessions="props.cancelledSessions"
          :total-sessions="props.totalCancelledSessions"
          user-type="tutor"
          :search-query="searchQuery"
          :subject-filter="subjectFilter"
          @session-click="openSessionDetails"
        />
      </div>
    </div>

    <!-- Session Details Modal -->
    <SessionDetailsModal
      :isOpen="isModalOpen"
      :session="modalSelectedSession"
      @close="closeModal"
      @navigateToFeedback="() => {}"
      @openMessages="() => {}"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import SessionSearchAndFilter from "@/components/Student-Tutor/SessionSearchAndFilter.vue";
import SessionDetailsModal from "@/components/Student-Tutor/SessionDetailsModal.vue";
import CancelledSessionsHistory from "@/components/Student-Tutor/CancelledSessionsHistory.vue";
import { BookOpenIcon, StarIcon, CheckIcon, SparklesIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";
import UserAvatar from "@/components/UserAvatar.vue";

const router = useRouter();

interface CompletedSession {
  id: string;
  studentName: string;
  studentProfileImage?: string | null;
  type: "face-to-face" | "online";
  subject: string;
  date: string;
  time: string;
  location?: string;
  yourRating: string;
  studentRating: string;
  feedback: string;
  feedbackStatus: "given" | "pending";
}

interface CancelledSession {
  id: string;
  studentName: string;
  studentProfileImage?: string | null;
  type: "face-to-face" | "online";
  status: "cancelled" | "declined";
  subject: string;
  date: string;
  time: string;
  location?: string;
  cancellationReason?: string;
  cancelledBy?: "student" | "tutor" | "admin";
  cancelledAt: string;
  // Allow additional properties from the stores
  [key: string]: any;
}

interface Props {
  completedSessions?: CompletedSession[];
  totalCompletedSessions?: number;
  cancelledSessions?: CancelledSession[];
  totalCancelledSessions?: number;
}

const props = withDefaults(defineProps<Props>(), {
  completedSessions: () => [],
  totalCompletedSessions: 0,
  cancelledSessions: () => [],
  totalCancelledSessions: 0,
});

const searchQuery = ref("");
const statusFilter = ref("all");
const subjectFilter = ref("all");

// Pagination state
const currentPage = ref(1);
const itemsPerPage = 4;

// Modal state for session details
const isModalOpen = ref(false);
const modalSelectedSession = ref<any>(null);

// Filter completed sessions based on search and filters
const filteredCompletedSessions = computed(() => {
  let filtered = props.completedSessions;

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(
      (session) =>
        session.studentName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        session.subject.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  // Apply subject filter
  if (subjectFilter.value !== "all") {
    filtered = filtered.filter((session) =>
      session.subject.toLowerCase().includes(subjectFilter.value.toLowerCase())
    );
  }

  return filtered;
});

// Get paginated sessions for current page
const paginatedCompletedSessions = computed(() => {
  return filteredCompletedSessions.value.slice(startIndex.value, endIndex.value);
});

// Pagination calculations for completed sessions
const totalPages = computed(() => Math.ceil(filteredCompletedSessions.value.length / itemsPerPage));
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage);
const endIndex = computed(() =>
  Math.min(startIndex.value + itemsPerPage, filteredCompletedSessions.value.length)
);

// Pagination methods for completed sessions
const goToPage = (page: number) => {
  currentPage.value = page;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

// Helper functions
const getTypeClasses = (type: string) => {
  switch (type) {
    case "face-to-face":
      return "bg-orange-100 text-orange-800 border-2 border-orange-800";
    case "online":
      return "bg-blue-100 text-blue-800 border-2 border-blue-800";
    default:
      return "bg-gray-100 text-gray-800 border-2 border-gray-800";
  }
};

const getFeedbackClasses = (feedbackStatus: string) => {
  switch (feedbackStatus) {
    case "given":
      return "bg-green-50 border border-green-200";
    case "pending":
      return "bg-yellow-50 border border-yellow-200";
    default:
      return "bg-gray-50 border border-gray-200";
  }
};

// Navigation function
const navigateToFeedback = (sessionId: string) => {
  router.push(`/feedback/${sessionId}`);
};

// Session details modal handlers
const openSessionDetails = (session: any) => {
  modalSelectedSession.value = session;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  modalSelectedSession.value = null;
};
</script>
